import React, { useState, useMemo, useEffect } from 'react';
import { Button, Space, Typography, Tag, Dropdown, MenuProps } from 'antd';
import { ReloadOutlined, PhoneOutlined, CalendarOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import InfiniteScrollList from '../Forms/ListView/InfiniteScrollList';
import { useDispatch } from 'react-redux';
import { getData } from '../../actions/apiCall';
import CreateActivityDropdown from '../CreateActivityDropdown';

const { Text, Title } = Typography;

export interface ActivitiesPopoverContentProps {
    recordId: string;
    modelName: string;
    onRefresh?: () => void;
    onCreateActivity?: (recordId: string, modelName: string, type?: string, subType?: string) => void;
    headerButton?: React.ReactNode;
}

const ActivitiesPopoverContent: React.FC<ActivitiesPopoverContentProps> = ({
    recordId,
    modelName,
    onRefresh,
    onCreateActivity,
    headerButton
}) => {
    const dispatch = useDispatch();

    // Navigation function - opens links in new tab
    const navigate = (path: string | number) => {
        if (typeof path === 'string') {
            window.open(path, '_blank');
        } else {
            window.history.back();
        }
    };
    const [activitiesFilter, setActivitiesFilter] = useState<'all' | 'task' | 'meeting' | 'call'>('all');
    const [reloadActivities, setReloadActivities] = useState(false);
    const [activitiesCounts, setActivitiesCounts] = useState<{ all: number; task: number; call: number; meeting: number }>({
        all: 0,
        task: 0,
        call: 0,
        meeting: 0
    });

    // Map model names to refType for activities API
    const getRefType = (modelName: string): string => {
        const mapping: { [key: string]: string } = {
            'leads': 'Leads',
            'contacts': 'Contacts',
            'accounts': 'Businesses',
            'deals': 'Deals',
        };
        return mapping[modelName.toLowerCase()] || modelName;
    };

    // Get activity icon based on type and status
    const getActivityIcon = (activity: any) => {
        if (activity.activityType === 'call') {
            return <PhoneOutlined style={{ color: '#1890ff' }} />;
        }
        if (activity.activityType === 'meeting') {
            return <CalendarOutlined style={{ color: '#722ed1' }} />;
        }
        // Task
        if (activity.displayStatus === 'Completed') {
            return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
        }
        return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
    };

    // Get activity status tag color
    const getStatusColor = (status: string, activityType?: string): string => {
        // Call status colors
        if (activityType === 'call') {
            const callStatusMap: { [key: string]: string } = {
                'Completed': 'green',
                'No Answer': 'orange',
                'Busy': 'orange',
                'Failed': 'red',
                'Missed': 'red',
            };
            return callStatusMap[status] || 'default';
        }

        // Meeting status colors
        if (activityType === 'meeting') {
            const meetingStatusMap: { [key: string]: string } = {
                'Scheduled': 'blue',
                'Completed': 'green',
                'Cancelled': 'red',
                'Rescheduled': 'orange',
            };
            return meetingStatusMap[status] || 'default';
        }

        // Task status colors
        const taskStatusMap: { [key: string]: string } = {
            'Completed': 'green',
            'In Progress': 'blue',
            'Not Started': 'default',
            'On Hold': 'orange',
            'Cancelled': 'red',
            'Deferred': 'orange',
            'Waiting for Response': 'orange',
        };
        return taskStatusMap[status] || 'default';
    };

    // Fetch activities counts on mount
    useEffect(() => {
        const fetchCounts = async () => {
            try {
                const refType = getRefType(modelName);
                const response = await dispatch(getData('/activities', {
                    refType: refType,
                    refId: recordId
                }));

                if (response && response.code === 200 && response.data && response.data.counts) {
                    setActivitiesCounts(response.data.counts);
                }
            } catch (error) {
                console.error('Error fetching activities counts:', error);
            }
        };
        fetchCounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordId, modelName]);

    // Handle reload trigger
    useEffect(() => {
        if (reloadActivities) {
            setReloadActivities(false);
        }
    }, [reloadActivities]);

    // Handle refresh
    const handleRefresh = () => {
        setReloadActivities(true);
        if (onRefresh) {
            onRefresh();
        }
    };

    // Build API query for InfiniteScrollList
    const apiQuery = useMemo(() => {
        const refType = getRefType(modelName);
        return {
            refType: refType,
            refId: recordId,
            filter: activitiesFilter === 'all' ? undefined : activitiesFilter
        };
    }, [recordId, modelName, activitiesFilter]);

    // Render activity item
    const renderActivityItem = (activity: any, index: number) => {
        const handleActivityClick = () => {
            if (!activity.id || !activity.activityType) return;

            // Navigate to activity detail page
            // Determine route based on activity type
            if (activity.activityType === 'task') {
                navigate(`/activities/task/${activity.id}`);
            } else if (activity.activityType === 'call') {
                if (activity.isScheduled) {
                    navigate(`/activities/call-schedule/${activity.id}`);
                } else {
                    navigate(`/activities/call-log/${activity.id}`);
                }
            } else if (activity.activityType === 'meeting') {
                navigate(`/activities/meeting/${activity.id}`);
            }
        };

        return (
            <div
                style={{
                    padding: '8px 12px',
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                }}
                onClick={handleActivityClick}
            >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: '#f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}>
                        {getActivityIcon(activity)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div>
                            <Text strong>{activity.displaySubject || 'Untitled Activity'}</Text>
                        </div>
                        <div style={{ marginTop: 4 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {moment(activity.displayDate).format('MMM DD, YYYY')}
                            </Text>
                            {activity.displayStatus && (
                                <>
                                    <Text type="secondary" style={{ margin: '0 8px' }}>•</Text>
                                    <Tag color={getStatusColor(activity.displayStatus, activity.activityType)}>
                                        {activity.displayStatus}
                                    </Tag>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div style={{ width: 400, maxHeight: 400, display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 12px',
                borderBottom: '1px solid #f0f0f0',
                flexShrink: 0,
                minHeight: 40
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flex: 1
                }}>
                    <Title level={5} style={{ margin: 0, lineHeight: '1.5' }}>Open Activities</Title>
                    <Button
                        type="text"
                        icon={<ReloadOutlined />}
                        size="small"
                        onClick={handleRefresh}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                            width: 24,
                            height: 24
                        }}
                    />
                </div>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end'
                }}>
                    {headerButton || (
                        <CreateActivityDropdown
                            recordId={recordId}
                            modelName={modelName}
                            onCreateActivity={onCreateActivity}
                            onTaskCreated={() => setReloadActivities(true)}
                            onScheduleCallCreated={() => setReloadActivities(true)}
                            onLogCallCreated={() => setReloadActivities(true)}
                        />
                    )}
                </div>
            </div>

            {/* Filter Tabs */}
            <div style={{
                display: 'flex',
                gap: 8,
                padding: '8px 12px',
                borderBottom: '1px solid #f0f0f0',
                flexShrink: 0
            }}>
                {[
                    { key: 'all', label: 'All' },
                    { key: 'task', label: 'Task' },
                    { key: 'meeting', label: 'Meeting' },
                    { key: 'call', label: 'Call' },
                ].map(filter => (
                    <Button
                        key={filter.key}
                        type={activitiesFilter === filter.key ? 'link' : 'text'}
                        size="small"
                        onClick={() => {
                            setActivitiesFilter(filter.key as any);
                        }}
                        style={{
                            padding: '0 8px',
                            borderBottom: activitiesFilter === filter.key ? '2px solid #722ed1' : 'none',
                            color: activitiesFilter === filter.key ? '#722ed1' : undefined,
                        }}
                    >
                        {filter.label} ({filter.key === 'all' ? activitiesCounts.all :
                            filter.key === 'task' ? activitiesCounts.task :
                                filter.key === 'call' ? activitiesCounts.call :
                                    activitiesCounts.meeting})
                    </Button>
                ))}
            </div>

            {/* Activities List with Infinite Scroll */}
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <InfiniteScrollList
                    apiUrl="/activities"
                    apiQuery={apiQuery}
                    renderItem={renderActivityItem}
                    height={250}
                    itemsPerPage={15}
                    reload={reloadActivities}
                    onLoaded={() => {
                        setReloadActivities(false);
                        // Fetch counts after loading
                        const fetchCounts = async () => {
                            try {
                                const refType = getRefType(modelName);
                                const response = await dispatch(getData('/activities', {
                                    refType: refType,
                                    refId: recordId,
                                    page: 1,
                                    limit: 1 // Just to get counts
                                }));
                                if (response && response.code === 200 && response.data && response.data.counts) {
                                    setActivitiesCounts(response.data.counts);
                                }
                            } catch (error) {
                                console.error('Error fetching activities counts:', error);
                            }
                        };
                        fetchCounts();
                    }}
                    listProps={{
                        locale: {
                            emptyText: <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>No activities found</div>
                        },
                        style: { padding: 0 }
                    }}
                />
            </div>
        </div>
    );
};

// CreateActivityDropdown is now imported from '../CreateActivityDropdown'

export default ActivitiesPopoverContent;
